import { useRef, useState } from "react";
import { Camera } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatErr } from "../lib/api";
import ImageCropper from "./ImageCropper";

/**
 * ImageUploader — pick file → crop → upload → return final URL via onChange.
 *
 * Props:
 *   value          current image URL
 *   onChange(url)  called with final hosted URL after upload
 *   aspect         crop aspect ratio (default 1)
 *   cropShape      "round" | "rect" (default "round")
 *   outputSize     {width, height} for output image (default 512x512)
 *   label          button label (default "Upload Photo")
 *   testid         data-testid prefix
 *   maxMb          max file size MB (default 10)
 *   previewClass   tailwind classes for the preview thumb (default square)
 *   placeholder    text to show when no image (default "?")
 */
export default function ImageUploader({
    value,
    onChange,
    aspect = 1,
    cropShape = "round",
    outputSize = { width: 512, height: 512 },
    label = "Upload Photo",
    testid = "img",
    maxMb = 10,
    previewClass = "w-20 h-20 rounded-sm",
    placeholder = "?",
}) {
    const fileRef = useRef(null);
    const [cropSrc, setCropSrc] = useState(null);
    const [busy, setBusy] = useState(false);

    const onFileSelected = (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        if (!f.type.startsWith("image/")) return toast.error("Pick an image file");
        if (f.size > maxMb * 1024 * 1024) return toast.error(`Max ${maxMb}MB`);
        const reader = new FileReader();
        reader.onload = () => setCropSrc(reader.result);
        reader.readAsDataURL(f);
    };

    const onCropConfirm = async (blob) => {
        setCropSrc(null);
        if (!blob) return;
        const form = new FormData();
        form.append("file", blob, "image.png");
        setBusy(true);
        try {
            const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
            const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
            onChange(fullUrl);
            toast.success("Photo ready");
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex items-center gap-4">
            <div className={`relative bg-wta-surface2 border border-wta-border overflow-hidden flex items-center justify-center font-heading text-2xl shrink-0 ${previewClass}`}>
                {value ? <img src={value} alt="" className="w-full h-full object-cover" /> : placeholder}
                {busy && <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-xs">…</div>}
            </div>
            <div className="flex-1 min-w-0">
                <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    onChange={onFileSelected}
                    className="hidden"
                    data-testid={`${testid}-file`}
                />
                <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={busy}
                    data-testid={`${testid}-button`}
                    className="btn-primary px-4 py-2 rounded-sm text-sm flex items-center gap-1 disabled:opacity-50"
                >
                    <Camera size={14} weight="fill" /> {busy ? "Uploading…" : label}
                </button>
                <div className="text-[10px] text-wta-muted mt-1">JPG, PNG, GIF · max {maxMb}MB</div>
            </div>

            {cropSrc && (
                <ImageCropper
                    src={cropSrc}
                    aspect={aspect}
                    cropShape={cropShape}
                    outputSize={outputSize}
                    onConfirm={onCropConfirm}
                    onCancel={() => setCropSrc(null)}
                />
            )}
        </div>
    );
}
